import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '@store';
import { EgeDateField, EgeMetric, EgeSource, EgeStatsFilters } from '@types';
import dayjs from 'dayjs';

/**
 * Filter and display state for the EGE statistics view.
 *
 * The FILTERS (dates, sources, countries, date field) decide what is fetched and are
 * shared by the map, the chart and the table -- switching tabs must not refetch or show
 * a different slice of data than the map above it.
 *
 * The DISPLAY options (per capita, log scale, panel tab) change only how the same data
 * is drawn, so they live here too but never reach the service.
 */
export interface StatsSliceState {
  startDate: string;
  endDate: string;
  sources: EgeSource[];
  /** ISO3. Empty means every country -- what an untouched multi-select means. */
  countries: string[];
  dateField: EgeDateField;

  // Display only.
  /**
   * Which number every widget shows.
   *
   * A display option rather than a filter, and that is a deliberate difference from
   * `dateField`: the service returns both metrics in one response because they cost the
   * same rows, so switching redraws what is already here instead of refetching.
   */
  metric: EgeMetric;
  /**
   * What the chart's x-axis is: calendar dates, whole months, or hour of day 0-23.
   *
   * TWO INVARIANTS, both maintained in the reducers so no sequence of user actions can
   * reach a state that is wrong or that the service would refuse:
   *
   * - 'hour' implies `dateField === 'record'`. There is no hourly delivery grain --
   *   delivery_date carries no time component -- and the service returns 400 for it.
   * - 'month' implies `metric === 'records'`. Record counts fold into a month exactly;
   *   distinct counts do not, because a device seen on twenty days is one device.
   */
  chartGroupBy: 'date' | 'month' | 'hour';
  perCapita: boolean;
  logScale: boolean;
  bottomPanelTab: 'chart' | 'table' | 'heatmap';
  /**
   * How much room the bottom panel takes.
   *
   * One enum rather than an `open` flag plus a `full` flag: those two booleans have four
   * combinations and only three of them mean anything, so the fourth would be a state
   * the UI has to decide what to do with.
   */
  bottomPanelSize: 'collapsed' | 'normal' | 'full';

  /**
   * True once the user has chosen a range themselves.
   *
   * Until then the range is a placeholder that `alignDateRangeToBounds` is free to
   * replace; afterwards it is the user's, and nothing moves it.
   */
  dateRangePinned: boolean;
}

/**
 * The default window: 30 days, ending at the LAST DAY THERE IS DATA FOR.
 *
 * Deliberately not "all time": the archive runs to 2025-01 and the whole of it in one
 * choropleth is both slow and unreadable.
 *
 * Just as deliberately not "30 days ending today". The feed trails the calendar by
 * several days and the archive is still being backfilled, so today is reliably outside
 * the data. The clock is only the placeholder used before the options endpoint answers;
 * `alignDateRangeToBounds` replaces it with the real window as soon as it does.
 */
const DEFAULT_DAYS = 30;

const initialState: StatsSliceState = {
  startDate: dayjs().subtract(DEFAULT_DAYS, 'day').format('YYYY-MM-DD'),
  endDate: dayjs().format('YYYY-MM-DD'),
  sources: ['bb', 'cc'],
  countries: [],
  dateField: 'record',
  metric: 'records',
  chartGroupBy: 'date',
  perCapita: false,
  logScale: false,
  bottomPanelTab: 'chart',
  bottomPanelSize: 'normal',
  dateRangePinned: false
};

/**
 * The range a "last N days" preset means.
 *
 * ANCHORED ON THE DATA, NOT THE CLOCK. The feed trails the calendar by several days and
 * the archive is backfilled, so a window ending today ends outside the bounds the picker
 * clamps to -- which is precisely the unclearable error state `alignDateRangeToBounds`
 * exists to prevent. A preset built on `dayjs()` would reintroduce it on every click.
 *
 * Inclusive of `maxDate`: 30 days covers it and the 29 before. Clamped at `minDate`, so
 * asking for more days than the archive holds gives the whole archive rather than a
 * range half of which has nothing behind it.
 */
export const presetRange = (days: number, minDate: string, maxDate: string): [string, string] => {
  const start = dayjs(maxDate)
    .subtract(days - 1, 'day')
    .format('YYYY-MM-DD');
  return [start < minDate ? minDate : start, maxDate];
};

export const statsSlice = createSlice({
  name: 'statsSlice',
  initialState,
  reducers: {
    setDateRange: (state, action: PayloadAction<[string, string]>) => {
      const [start, end] = action.payload;
      // Normalised on the way in so no consumer has to wonder: the service rejects a
      // range whose end precedes its start, and a picker can produce one mid-drag.
      state.startDate = start <= end ? start : end;
      state.endDate = start <= end ? end : start;
      state.dateRangePinned = true;
    },

    /**
     * Move the placeholder range onto the window the data actually covers.
     *
     * Dispatched when the options endpoint answers. Without this the initial range is
     * whatever the clock said, which the picker then flags as out of range -- an error
     * the user never caused and cannot clear, because the value it objects to is the
     * one this slice supplied.
     *
     * A range the user picked is left alone: they are allowed to look at an empty
     * window, and having the app silently move their dates would be worse than the
     * error this fixes.
     */
    alignDateRangeToBounds: (state, action: PayloadAction<{ minDate: string; maxDate: string }>) => {
      if (state.dateRangePinned) return;
      const { minDate, maxDate } = action.payload;
      // Inclusive: a 30-day window covers maxDate and the 29 days before it.
      const start = dayjs(maxDate)
        .subtract(DEFAULT_DAYS - 1, 'day')
        .format('YYYY-MM-DD');
      state.startDate = start < minDate ? minDate : start;
      state.endDate = maxDate;
    },
    setSources: (state, action: PayloadAction<EgeSource[]>) => {
      state.sources = action.payload;
    },
    setCountries: (state, action: PayloadAction<string[]>) => {
      state.countries = action.payload;
    },
    setDateField: (state, action: PayloadAction<EgeDateField>) => {
      state.dateField = action.payload;
      // Delivery has no hourly grain, so asking for it drops the hourly views rather
      // than leaving a combination the service refuses. See `chartGroupBy`.
      if (action.payload === 'delivery') {
        state.chartGroupBy = 'date';
        if (state.bottomPanelTab === 'heatmap') state.bottomPanelTab = 'chart';
      }
    },

    setChartGroupBy: (state, action: PayloadAction<StatsSliceState['chartGroupBy']>) => {
      state.chartGroupBy = action.payload;
      // Choosing hours pins the date field to record, because record is the only
      // bucketing that HAS hours.
      if (action.payload === 'hour') state.dateField = 'record';
      // Choosing months pins the metric to records, the only one that folds exactly.
      if (action.payload === 'month') state.metric = 'records';
    },
    setMetric: (state, action: PayloadAction<EgeMetric>) => {
      state.metric = action.payload;
      // The other half: a distinct metric cannot be shown by month, so asking for one
      // drops the chart back to dates rather than leaving a figure 5x-9x too large.
      //
      // Reachable even though the month view disables these options -- the metric is
      // shared, so it can be changed from the table or heatmap while the chart is
      // sitting in month mode behind them.
      if (action.payload !== 'records' && state.chartGroupBy === 'month') state.chartGroupBy = 'date';
    },
    setPerCapita: (state, action: PayloadAction<boolean>) => {
      state.perCapita = action.payload;
    },
    setLogScale: (state, action: PayloadAction<boolean>) => {
      state.logScale = action.payload;
    },
    setBottomPanelTab: (state, action: PayloadAction<'chart' | 'table' | 'heatmap'>) => {
      state.bottomPanelTab = action.payload;
      // The heatmap is hourly, and hourly exists only for record dates. Same invariant
      // as `chartGroupBy === 'hour'` -- see the field's comment.
      if (action.payload === 'heatmap') state.dateField = 'record';
    },
    setBottomPanelSize: (state, action: PayloadAction<StatsSliceState['bottomPanelSize']>) => {
      state.bottomPanelSize = action.payload;
    }
  }
});

export const {
  setDateRange,
  alignDateRangeToBounds,
  setSources,
  setCountries,
  setDateField,
  setMetric,
  setChartGroupBy,
  setPerCapita,
  setLogScale,
  setBottomPanelTab,
  setBottomPanelSize
} = statsSlice.actions;

const selectStats = (state: RootState) => state.statsSlice;

/**
 * The filters, in the shape the service takes.
 *
 * Memoised because it is the ARGUMENT to every RTK Query hook in this view: a fresh
 * object each render would look like a new cache key and refetch on every keystroke.
 */
export const selectEgeStatsFilters = createSelector([selectStats], (stats): EgeStatsFilters => ({
  startDate: stats.startDate,
  endDate: stats.endDate,
  sources: stats.sources,
  countries: stats.countries,
  dateField: stats.dateField
}));

export const selectDisplayOptions = createSelector([selectStats], (stats) => ({
  metric: stats.metric,
  chartGroupBy: stats.chartGroupBy,
  perCapita: stats.perCapita,
  logScale: stats.logScale,
  bottomPanelTab: stats.bottomPanelTab,
  bottomPanelSize: stats.bottomPanelSize
}));

export default statsSlice.reducer;
